// FILE: MessagesTimeline.logic.ts
// Purpose: Owns the pure row-derivation helpers used by the transcript hot path.
// Layer: Web chat presentation helpers
// Exports: row derivation, structural sharing, copy/timer and author helpers

import {
  type MessageId,
  type OrchestrationLatestTurn,
  type TurnId,
  type UserId,
} from "@taskin-code/contracts";
import { formatDuration, type TimelineEntry, type WorkLogEntry } from "../../session-logic";
import { normalizeCompactToolLabel as normalizeCompactToolLabelValue } from "../../lib/toolCallLabel";
import { type ChatMessage, type ProposedPlan, type TurnDiffSummary } from "../../types";

export interface TimelineDurationMessage {
  id: string;
  role: "user" | "assistant" | "system";
  createdAt: string;
  turnId?: string | null;
  completedAt?: string | undefined;
}

export type MessagesTimelineRow =
  | {
      kind: "work";
      id: string;
      createdAt: string;
      groupedEntries: WorkLogEntry[];
      // True while the turn is still running and this is the trailing group, i.e. it
      // can still receive entries. The transcript keeps those groups open so progress
      // stays visible, and collapses them once the turn settles.
      isActive?: boolean;
    }
  | {
      kind: "message";
      id: string;
      createdAt: string;
      message: ChatMessage;
      inlineWorkEntries?: WorkLogEntry[];
      inlineWorkGroupId?: string;
      inlineWorkActive?: boolean;
      durationStart: string;
      showAssistantCopyButton: boolean;
      assistantTurnDiffSummary?: TurnDiffSummary | undefined;
      revertTurnCount?: number | undefined;
    }
  | {
      kind: "proposed-plan";
      id: string;
      createdAt: string;
      proposedPlan: ProposedPlan;
    }
  | {
      kind: "turn-fold";
      id: string;
      createdAt: string;
      turnId: TurnId;
      label: string;
      expanded: boolean;
    }
  | { kind: "working"; id: string; createdAt: string | null; label?: string | undefined }
  // The thread recap: one line describing where the thread stands, regenerated
  // when a turn settles. It is a property of the thread, not of the turn it
  // followed, so it always sits at the tail of the transcript rather than being
  // anchored to a point in the history.
  | { kind: "recap"; id: string; createdAt: string; text: string };

export interface StableMessagesTimelineRowsState {
  byId: Map<string, MessagesTimelineRow>;
  result: MessagesTimelineRow[];
}

export function computeMessageDurationStart(
  messages: ReadonlyArray<TimelineDurationMessage>,
): Map<string, string> {
  const result = new Map<string, string>();
  let lastBoundary: string | null = null;

  for (const message of messages) {
    if (message.role === "user") {
      lastBoundary = message.createdAt;
    }
    result.set(message.id, lastBoundary ?? message.createdAt);
    if (message.role === "assistant" && message.completedAt) {
      lastBoundary = message.completedAt;
    }
  }

  return result;
}

export function normalizeCompactToolLabel(value: string): string {
  return normalizeCompactToolLabelValue(value);
}

// Only pure tool-call runs collapse behind a single summary line; a group that also
// carries reasoning, info or error rows keeps every row visible, since hiding those
// would hide the reason a turn went wrong.
export function hasOnlyToolToneEntries<T extends { tone: WorkLogEntry["tone"] }>(
  entries: ReadonlyArray<T> | undefined,
): entries is ReadonlyArray<T> {
  if (!entries || entries.length === 0) {
    return false;
  }
  return entries.every((entry) => entry.tone === "tool");
}

export function resolveAssistantMessageCopyState({
  text,
  showCopyButton,
  streaming,
}: {
  text: string | null;
  showCopyButton: boolean;
  streaming: boolean;
}) {
  const normalizedText = text?.trim() ? text : null;
  return {
    text: normalizedText,
    visible: showCopyButton && normalizedText !== null && !streaming,
  };
}

export function deriveTerminalAssistantMessageIds(
  messages: ReadonlyArray<TimelineDurationMessage>,
): Set<string> {
  const lastAssistantMessageIdByResponseKey = new Map<string, string>();
  let nullTurnResponseIndex = 0;

  for (const message of messages) {
    if (message.role === "user") {
      nullTurnResponseIndex += 1;
      continue;
    }
    if (message.role !== "assistant") {
      continue;
    }

    const responseKey = message.turnId
      ? `turn:${message.turnId}`
      : `unkeyed:${nullTurnResponseIndex}`;
    lastAssistantMessageIdByResponseKey.set(responseKey, message.id);
  }

  return new Set(lastAssistantMessageIdByResponseKey.values());
}

/** The turn fields the fold needs; a subset of the projection's latest turn. */
export type TimelineLatestTurn = Pick<
  OrchestrationLatestTurn,
  "turnId" | "state" | "startedAt" | "completedAt"
>;

interface TurnFold {
  turnId: TurnId;
  anchorEntryId: string;
  createdAt: string;
  hiddenEntryIds: ReadonlySet<string>;
  label: string;
}

function timelineEntryOwningTurnId(entry: TimelineEntry): TurnId | null {
  if (entry.kind === "message") {
    return entry.message.role === "assistant" ? (entry.message.turnId ?? null) : null;
  }
  return entry.kind === "work" ? (entry.entry.turnId ?? null) : null;
}

/** When the entry stopped producing output, for the purpose of measuring a turn. */
function timelineEntryEndedAt(entry: TimelineEntry): string {
  return entry.kind === "message"
    ? (entry.message.completedAt ?? entry.createdAt)
    : entry.createdAt;
}

function computeElapsedMs(startIso: string, endIso: string): number | null {
  const startedAt = Date.parse(startIso);
  const endedAt = Date.parse(endIso);
  if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt < startedAt) {
    return null;
  }
  return endedAt - startedAt;
}

function maxIsoTimestamp(left: string | null | undefined, right: string): string {
  return left && left > right ? left : right;
}

/**
 * The turn that must never fold, because it is not finished being written.
 *
 * Deliberately keyed on the turn's lifecycle rather than on transient working
 * state: right after the user sends a message the previous turn is still the
 * latest one until the server creates the new one, and folding must not
 * flicker through that window.
 */
export function deriveUnsettledTurnId(
  latestTurn: TimelineLatestTurn | null,
  latestTurnSettled: boolean,
): TurnId | null {
  if (!latestTurn) {
    return null;
  }
  return latestTurnSettled ? null : latestTurn.turnId;
}

/**
 * Settled turns keep only their terminal assistant message visible.
 *
 * Everything before it — reasoning, tool calls, progress — folds behind a
 * "Trabajado por ..." row anchored at the first hidden entry, so the duration
 * leads directly into the final response and the transcript reads as an
 * answer instead of a log. Expanding a fold brings every row back.
 */
export function deriveTurnFolds(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  terminalAssistantMessageIds: ReadonlySet<string>;
  latestTurn: TimelineLatestTurn | null;
  unsettledTurnId: TurnId | null;
}): ReadonlyMap<string, TurnFold> {
  interface TurnGroup {
    entries: Array<TimelineEntry>;
    terminalEntry: Extract<TimelineEntry, { kind: "message" }> | null;
    hasStreamingMessage: boolean;
    /**
     * The user message that kicked the turn off. Entry timestamps alone
     * undercount the duration — the first entry only appears once the provider
     * starts producing output — and a turn cut short may hold a single
     * instantaneous message.
     */
    startBoundary: string | null;
  }

  const groupsByTurnId = new Map<TurnId, TurnGroup>();
  let pendingUserBoundary: string | null = null;

  for (const entry of input.timelineEntries) {
    if (entry.kind === "message" && entry.message.role === "user") {
      pendingUserBoundary = entry.message.createdAt;
      continue;
    }
    const turnId = timelineEntryOwningTurnId(entry);
    if (!turnId) {
      continue;
    }
    let group = groupsByTurnId.get(turnId);
    if (!group) {
      group = {
        entries: [],
        terminalEntry: null,
        hasStreamingMessage: false,
        // Each user boundary starts at most one turn; a second turn after the
        // same user message falls back to its own first entry.
        startBoundary: pendingUserBoundary,
      };
      pendingUserBoundary = null;
      groupsByTurnId.set(turnId, group);
    }
    group.entries.push(entry);
    if (entry.kind === "message") {
      if (input.terminalAssistantMessageIds.has(entry.message.id)) {
        group.terminalEntry = entry;
      }
      if (entry.message.streaming) {
        group.hasStreamingMessage = true;
      }
    }
  }

  const foldsByAnchorEntryId = new Map<string, TurnFold>();
  for (const [turnId, group] of groupsByTurnId) {
    if (turnId === input.unsettledTurnId || group.hasStreamingMessage) {
      continue;
    }

    const hiddenEntryIds = new Set<string>();
    for (const entry of group.entries) {
      if (entry.id === group.terminalEntry?.id) {
        continue;
      }
      // Delegation rows never fold: a subagent outlives the turn that spawned
      // it, and hiding the row when that turn settles makes a still-running
      // delegation invisible.
      if (entry.kind === "work" && entry.entry.subagentTask !== undefined) {
        continue;
      }
      hiddenEntryIds.add(entry.id);
    }
    if (hiddenEntryIds.size === 0) {
      continue;
    }

    const firstEntry = group.entries[0];
    const firstHiddenEntry = group.entries.find((entry) => hiddenEntryIds.has(entry.id));
    const lastEntry = group.entries.at(-1);
    if (!firstEntry || !firstHiddenEntry || !lastEntry) {
      continue;
    }

    const isLatestTurn = input.latestTurn?.turnId === turnId;
    // A turn cut short leaves trailing work entries behind its terminal
    // message — take whichever ended last.
    const lastEntryEnd = timelineEntryEndedAt(lastEntry);
    const elapsedMs =
      isLatestTurn && input.latestTurn?.startedAt && input.latestTurn.completedAt
        ? computeElapsedMs(input.latestTurn.startedAt, input.latestTurn.completedAt)
        : computeElapsedMs(
            group.startBoundary ?? firstEntry.createdAt,
            maxIsoTimestamp(group.terminalEntry?.message.completedAt ?? null, lastEntryEnd),
          );
    const duration = elapsedMs !== null ? formatDuration(elapsedMs) : null;
    const label =
      isLatestTurn && input.latestTurn?.state === "interrupted"
        ? duration
          ? `Detenido tras ${duration}`
          : "Detuviste esta respuesta"
        : duration
          ? `Trabajado por ${duration}`
          : "Trabajado";

    foldsByAnchorEntryId.set(firstHiddenEntry.id, {
      turnId,
      anchorEntryId: firstHiddenEntry.id,
      createdAt: firstHiddenEntry.createdAt,
      hiddenEntryIds,
      label,
    });
  }

  return foldsByAnchorEntryId;
}

// Derives transcript rows from timeline entries while preserving the current
// t3code behavior of attaching trailing work groups to the adjacent assistant reply.
export function deriveMessagesTimelineRows(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  isWorking: boolean;
  activeTurnStartedAt: string | null;
  turnDiffSummaryByAssistantMessageId: ReadonlyMap<MessageId, TurnDiffSummary>;
  revertTurnCountByUserMessageId: ReadonlyMap<MessageId, number>;
  latestTurn?: TimelineLatestTurn | null;
  latestTurnSettled?: boolean;
  expandedTurnIds?: ReadonlySet<TurnId>;
  recap?: { text: string; generatedAt: string } | null | undefined;
}): MessagesTimelineRow[] {
  const nextRows: MessagesTimelineRow[] = [];
  const timelineMessages = input.timelineEntries.flatMap((entry) =>
    entry.kind === "message" ? [entry.message] : [],
  );
  const durationStartByMessageId = computeMessageDurationStart(timelineMessages);
  const terminalAssistantMessageIds = deriveTerminalAssistantMessageIds(timelineMessages);
  const unsettledTurnId = deriveUnsettledTurnId(
    input.latestTurn ?? null,
    input.latestTurnSettled ?? false,
  );
  // No latest turn means nothing is known about which turn is still alive, and
  // folding the running one would be the worst possible mistake — so a caller
  // that does not supply it gets no folds at all.
  const foldsByAnchorEntryId = input.latestTurn
    ? deriveTurnFolds({
        timelineEntries: input.timelineEntries,
        terminalAssistantMessageIds,
        latestTurn: input.latestTurn,
        unsettledTurnId,
      })
    : new Map<string, TurnFold>();

  const foldedTurnIds = new Set<TurnId>();
  for (const fold of foldsByAnchorEntryId.values()) {
    foldedTurnIds.add(fold.turnId);
  }

  // An expanded fold keeps its row (so it can be collapsed again) but hides
  // nothing, so its entries never enter `collapsedEntryIds`.
  const collapsedEntryIds = new Set<string>();
  for (const fold of foldsByAnchorEntryId.values()) {
    if (input.expandedTurnIds?.has(fold.turnId)) continue;
    for (const entryId of fold.hiddenEntryIds) {
      collapsedEntryIds.add(entryId);
    }
  }

  // Walking the visible entries only keeps the work-grouping loop below honest:
  // a collapsed entry must not join a group, or it would resurface inside the
  // summary of the group it belongs to.
  const walkEntries: TimelineEntry[] = [];
  const foldsBeforeEntryId = new Map<string, Array<TurnFold>>();
  const trailingFolds: Array<TurnFold> = [];
  let pendingFolds: Array<TurnFold> = [];
  for (const entry of input.timelineEntries) {
    const anchoredFold = foldsByAnchorEntryId.get(entry.id);
    if (anchoredFold) {
      pendingFolds.push(anchoredFold);
    }
    if (collapsedEntryIds.has(entry.id)) {
      continue;
    }
    if (pendingFolds.length > 0) {
      foldsBeforeEntryId.set(entry.id, pendingFolds);
      pendingFolds = [];
    }
    walkEntries.push(entry);
  }
  trailingFolds.push(...pendingFolds);

  let pendingWorkGroup: Extract<MessagesTimelineRow, { kind: "work" }> | null = null;

  const groupedEntriesEqual = (
    left: ReadonlyArray<WorkLogEntry>,
    right: ReadonlyArray<WorkLogEntry>,
  ) => left.length === right.length && left.every((entry, index) => entry === right[index]);

  const appendWorkEntriesToPreviousAssistant = (
    groupedEntries: WorkLogEntry[],
    groupId: string,
  ): boolean => {
    const previousRow = nextRows.at(-1);
    if (
      !previousRow ||
      previousRow.kind !== "message" ||
      previousRow.message.role !== "assistant"
    ) {
      return false;
    }

    // Trailing groups attach to the answer above them; without an id the transcript
    // would have no key to remember whether the user expanded them.
    previousRow.inlineWorkGroupId ??= groupId;

    const nextInlineWorkEntries = previousRow.inlineWorkEntries
      ? [...previousRow.inlineWorkEntries, ...groupedEntries]
      : groupedEntries;

    if (groupedEntriesEqual(previousRow.inlineWorkEntries ?? [], nextInlineWorkEntries)) {
      return true;
    }

    previousRow.inlineWorkEntries = nextInlineWorkEntries;
    return true;
  };

  const flushPendingWorkGroup = (options?: { attachToPreviousAssistant?: boolean }) => {
    if (!pendingWorkGroup) return;
    // Work belonging to a folded turn stays a row of its own. Attaching it to
    // the answer would put it back inside the very message the fold exists to
    // keep clean, and the inline renderer collapses a mixed run (reasoning plus
    // tools) down to a one-line summary — so expanding the fold would reveal
    // nothing.
    const belongsToFoldedTurn = pendingWorkGroup.groupedEntries.some(
      (entry) => entry.turnId !== undefined && foldedTurnIds.has(entry.turnId),
    );
    const shouldAttachToPreviousAssistant =
      !belongsToFoldedTurn && (options?.attachToPreviousAssistant ?? true);
    if (
      !shouldAttachToPreviousAssistant ||
      !appendWorkEntriesToPreviousAssistant(pendingWorkGroup.groupedEntries, pendingWorkGroup.id)
    ) {
      nextRows.push(pendingWorkGroup);
    }
    pendingWorkGroup = null;
  };

  const emitFoldRows = (folds: ReadonlyArray<TurnFold> | undefined) => {
    if (!folds) return;
    for (const fold of folds) {
      // The fold row opens the hidden run, so nothing may still be buffered
      // behind it — flushing keeps the visible order faithful to the timeline.
      flushPendingWorkGroup();
      nextRows.push({
        kind: "turn-fold",
        id: `turn-fold:${fold.turnId}`,
        createdAt: fold.createdAt,
        turnId: fold.turnId,
        label: fold.label,
        expanded: input.expandedTurnIds?.has(fold.turnId) ?? false,
      });
    }
  };

  for (let index = 0; index < walkEntries.length; index += 1) {
    const timelineEntry = walkEntries[index];
    if (!timelineEntry) {
      continue;
    }

    emitFoldRows(foldsBeforeEntryId.get(timelineEntry.id));

    if (timelineEntry.kind === "work") {
      const groupedEntries = [timelineEntry.entry];
      let cursor = index + 1;
      while (cursor < walkEntries.length) {
        const nextEntry = walkEntries[cursor];
        if (!nextEntry || nextEntry.kind !== "work") break;
        // A fold row belongs between these two entries, so the run ends here
        // rather than swallowing the boundary.
        if (foldsBeforeEntryId.has(nextEntry.id)) break;
        groupedEntries.push(nextEntry.entry);
        cursor += 1;
      }
      flushPendingWorkGroup();
      pendingWorkGroup = {
        kind: "work",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        groupedEntries,
      };
      index = cursor - 1;
      continue;
    }

    if (timelineEntry.kind === "proposed-plan") {
      flushPendingWorkGroup();
      nextRows.push({
        kind: "proposed-plan",
        id: timelineEntry.id,
        createdAt: timelineEntry.createdAt,
        proposedPlan: timelineEntry.proposedPlan,
      });
      continue;
    }

    // Same rule at the answer itself: a folded turn's work never rides inside
    // the message row.
    const pendingBelongsToFoldedTurn =
      pendingWorkGroup?.groupedEntries.some(
        (entry) => entry.turnId !== undefined && foldedTurnIds.has(entry.turnId),
      ) ?? false;
    const canInlinePendingWork =
      timelineEntry.message.role === "assistant" && !pendingBelongsToFoldedTurn;
    const inlineWorkEntries = canInlinePendingWork ? pendingWorkGroup?.groupedEntries : undefined;
    const inlineWorkGroupId = canInlinePendingWork ? pendingWorkGroup?.id : undefined;
    if (canInlinePendingWork) {
      pendingWorkGroup = null;
    } else {
      flushPendingWorkGroup();
    }

    nextRows.push({
      kind: "message",
      id: timelineEntry.id,
      createdAt: timelineEntry.createdAt,
      message: timelineEntry.message,
      ...(inlineWorkEntries ? { inlineWorkEntries } : {}),
      ...(inlineWorkGroupId ? { inlineWorkGroupId } : {}),
      durationStart:
        durationStartByMessageId.get(timelineEntry.message.id) ?? timelineEntry.message.createdAt,
      showAssistantCopyButton:
        timelineEntry.message.role === "assistant" &&
        terminalAssistantMessageIds.has(timelineEntry.message.id),
      assistantTurnDiffSummary:
        timelineEntry.message.role === "assistant"
          ? input.turnDiffSummaryByAssistantMessageId.get(timelineEntry.message.id)
          : undefined,
      revertTurnCount:
        timelineEntry.message.role === "user"
          ? input.revertTurnCountByUserMessageId.get(timelineEntry.message.id)
          : undefined,
    });
  }

  // Keep any trailing work summary visually attached to the last answer so a
  // completed chat does not end with a detached tool-log footer.
  flushPendingWorkGroup();

  // A turn that never produced a terminal message folds away entirely, leaving
  // its "Trabajado por ..." row with nothing behind it to anchor against.
  emitFoldRows(trailingFolds);

  // A recap describes a settled thread, so a running turn already invalidates it:
  // it is dropped rather than shown stale, and the next settle regenerates one.
  // After `emitFoldRows`, so a folded tail cannot end up below it.
  if (input.recap && !input.isWorking && nextRows.length > 0) {
    nextRows.push({
      kind: "recap",
      id: "thread-recap-row",
      createdAt: input.recap.generatedAt,
      text: input.recap.text,
    });
  }

  if (input.isWorking) {
    markTrailingWorkGroupActive(nextRows);
    const latestWorkEntry = input.timelineEntries
      .toReversed()
      .find(
        (entry) =>
          entry.kind === "work" && isWorkingLabelSource(entry.entry, input.activeTurnStartedAt),
      );
    nextRows.push({
      kind: "working",
      id: "working-indicator-row",
      createdAt: input.activeTurnStartedAt,
      ...(latestWorkEntry?.kind === "work" ? { label: latestWorkEntry.entry.label } : {}),
    });
  }

  return nextRows;
}

/**
 * Decides whether a work row may lend its label to the "working" indicator.
 *
 * The indicator answers "what is the agent doing right now", so its label has
 * to come from work belonging to the turn that is actually running. Two kinds
 * of row are disqualified:
 *
 *   - boundary rows (`/clear`, a manual `/compact`), which describe the
 *     conversation and outlive every turn. This is the bug users saw: after
 *     compacting once, every later message was announced as "Compacting
 *     conversation...", because the compaction row stayed the newest work row
 *     in the timeline;
 *   - rows older than the current turn, which matter while the turn has not
 *     produced any work of its own yet — with no started-at to compare against
 *     there is no turn to be wrong about, so the newest row is used as before.
 */
function isWorkingLabelSource(entry: WorkLogEntry, activeTurnStartedAt: string | null): boolean {
  if (entry.isBoundary) {
    return false;
  }
  if (!activeTurnStartedAt) {
    return true;
  }
  return entry.createdAt >= activeTurnStartedAt;
}

// The trailing work group is the only one that can still grow while the turn runs,
// so it is the only one the transcript keeps expanded on its own.
function markTrailingWorkGroupActive(rows: MessagesTimelineRow[]): void {
  const lastRow = rows.at(-1);
  if (!lastRow) {
    return;
  }
  if (lastRow.kind === "work") {
    lastRow.isActive = true;
    return;
  }
  if (lastRow.kind === "message" && (lastRow.inlineWorkEntries?.length ?? 0) > 0) {
    lastRow.inlineWorkActive = true;
  }
}

// Reuses stable row references so streaming updates only invalidate rows whose
// visible content actually changed.
export function computeStableMessagesTimelineRows(
  rows: MessagesTimelineRow[],
  previous: StableMessagesTimelineRowsState,
): StableMessagesTimelineRowsState {
  const next = new Map<string, MessagesTimelineRow>();
  let anyChanged = rows.length !== previous.byId.size;

  const result = rows.map((row, index) => {
    const prevRow = previous.byId.get(row.id);
    const nextRow = prevRow && isRowUnchanged(prevRow, row) ? prevRow : row;
    next.set(row.id, nextRow);
    if (!anyChanged && previous.result[index] !== nextRow) {
      anyChanged = true;
    }
    return nextRow;
  });

  return anyChanged ? { byId: next, result } : previous;
}

function shallowEqualEntryArray<T>(
  left: ReadonlyArray<T> | undefined,
  right: ReadonlyArray<T> | undefined,
) {
  if (left === right) return true;
  if (!left || !right) return false;
  return left.length === right.length && left.every((entry, index) => entry === right[index]);
}

function isRowUnchanged(a: MessagesTimelineRow, b: MessagesTimelineRow): boolean {
  if (a.kind !== b.kind || a.id !== b.id) return false;

  switch (a.kind) {
    case "working":
      return a.createdAt === (b as typeof a).createdAt;

    case "recap":
      return a.createdAt === (b as typeof a).createdAt && a.text === (b as typeof a).text;

    case "proposed-plan":
      return a.proposedPlan === (b as typeof a).proposedPlan;

    case "turn-fold": {
      const bf = b as typeof a;
      return a.createdAt === bf.createdAt && a.label === bf.label && a.expanded === bf.expanded;
    }

    case "work":
      return (
        a.createdAt === (b as typeof a).createdAt &&
        a.isActive === (b as typeof a).isActive &&
        shallowEqualEntryArray(a.groupedEntries, (b as typeof a).groupedEntries)
      );

    case "message": {
      const bm = b as typeof a;
      return (
        a.message === bm.message &&
        shallowEqualEntryArray(a.inlineWorkEntries, bm.inlineWorkEntries) &&
        a.inlineWorkGroupId === bm.inlineWorkGroupId &&
        a.inlineWorkActive === bm.inlineWorkActive &&
        a.durationStart === bm.durationStart &&
        a.showAssistantCopyButton === bm.showAssistantCopyButton &&
        a.assistantTurnDiffSummary === bm.assistantTurnDiffSummary &&
        a.revertTurnCount === bm.revertTurnCount
      );
    }
  }
}

/** What the chrome row needs to draw the author of a message. */
export interface MessageAuthorPresentation {
  userId: UserId | null;
  displayName: string;
  avatarFileName: string | null;
}

/**
 * Resolves who to show next to a message.
 *
 * `authorDisplayName` is a query-time convenience the server adds by joining
 * the users table, so it only exists on messages that arrived through a
 * snapshot. Live events carry the stored fact — the author's id — and nothing
 * else, so the name is looked up in the user directory the client already
 * keeps. Returns null when there is nobody to attribute the message to, which
 * is the normal case for assistant messages and for installs without auth.
 */
export function resolveMessageAuthorPresentation(
  message: {
    authorUserId?: UserId | null | undefined;
    authorDisplayName?: string | null | undefined;
  },
  userDirectory: ReadonlyMap<UserId, { displayName: string; avatarFileName: string | null }>,
): MessageAuthorPresentation | null {
  const userId = message.authorUserId ?? null;
  const directoryEntry = userId ? userDirectory.get(userId) : undefined;
  const displayName = message.authorDisplayName ?? directoryEntry?.displayName ?? null;
  if (!displayName) {
    return null;
  }
  return {
    userId,
    displayName,
    avatarFileName: directoryEntry?.avatarFileName ?? null,
  };
}

export interface WorkEntryAttachmentImage {
  attachmentId: string;
  /** File name as the device reported it, used as the caption and the alt text. */
  name: string;
  /** Route the server serves the stored attachment from. */
  path: string;
}

/**
 * Attachment ids are `[a-z0-9_-]+` (see `ChatAttachmentId` in contracts). Matching
 * that charset rather than `.+` means a mangled line never becomes an `<img src>`.
 */
const WORK_ENTRY_ATTACHMENT_LINE_REGEX =
  /^-[ \t]+(.+?)[ \t]+\(\d+ bytes\)[ \t]+→[ \t]+\/attachments\/([A-Za-z0-9_-]+)[ \t]*$/;

/**
 * A tool row shows at most this many previews. The server already clamps how many
 * images it forwards to the model (`AGENT_TOOL_MAX_IMAGES`); this is the same
 * ceiling applied again on the transcript, so a misbehaving tool cannot make one
 * row fetch an unbounded number of attachments.
 */
export const WORK_ENTRY_MAX_ATTACHMENT_IMAGES = 4;

/**
 * How tall one capture preview can get in a tool row. Single source of truth:
 * `WorkEntryAttachmentImages` caps the `<img>` with it, and `timelineHeight.ts`
 * budgets the same number before the real image has loaded. Two copies of this
 * value would mean a row that jumps the moment a capture arrives — exactly what
 * the height estimator exists to prevent.
 */
export const WORK_ENTRY_ATTACHMENT_PREVIEW_MAX_HEIGHT_PX = 160;

/**
 * Finds the captures a local action uploaded, from the very lines the tool result
 * already shows the reader. Deliberately anchored to that one line format instead
 * of scanning for any `/attachments/…` substring: a tool that merely mentions a
 * path in its output should not sprout an image preview.
 *
 * Server-side truncation can cut a line in half, and then it simply does not match
 * — one preview fewer, never a broken `<img>`.
 */
export function extractWorkEntryAttachmentImages(
  output: string | undefined,
): ReadonlyArray<WorkEntryAttachmentImage> {
  if (output === undefined || !output.includes("/attachments/")) {
    return [];
  }
  const images: WorkEntryAttachmentImage[] = [];
  const seen = new Set<string>();
  for (const line of output.split("\n")) {
    const match = WORK_ENTRY_ATTACHMENT_LINE_REGEX.exec(line.trimEnd());
    if (match === null) continue;
    const name = match[1]?.trim() ?? "";
    const attachmentId = match[2] ?? "";
    if (name.length === 0 || attachmentId.length === 0 || seen.has(attachmentId)) continue;
    seen.add(attachmentId);
    images.push({ attachmentId, name, path: `/attachments/${attachmentId}` });
    if (images.length === WORK_ENTRY_MAX_ATTACHMENT_IMAGES) break;
  }
  return images;
}

/**
 * Whether a run of tool rows carries a capture meant for the reader's eyes.
 *
 * A settled group of pure tool calls folds itself behind a summary line — see
 * `hasOnlyToolToneEntries` — because tool traffic is noise. A capture the user
 * asked for is not noise, it is the answer: folding it away leaves the agent
 * announcing an image nobody can see. Such a group stays collapsible by hand,
 * it just does not collapse on its own.
 *
 * Cheap enough for the virtualizer's size estimator, which calls this per row:
 * `extractWorkEntryAttachmentImages` bails on a substring check before it ever
 * splits lines, and `some` stops at the first capture.
 */
export function hasWorkEntryAttachmentImages<T extends { output?: string | undefined }>(
  entries: ReadonlyArray<T> | undefined,
): boolean {
  if (!entries) return false;
  return entries.some((entry) => extractWorkEntryAttachmentImages(entry.output).length > 0);
}
